import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight, Download, Loader2 } from 'lucide-react'

import { RefreshButton } from './RefreshButton'

import { api } from '@/api/client'
import { useFileDownload } from '@/lib/useFileDownload'
import { useToast } from '@/stores/toast'
import { useTabField } from '@/stores/tabState'
import { MultiPartnerPicker } from './MultiPartnerPicker'
import { SearchBox } from './SearchBox'
import { DateRangeBar } from './DateRangeBar'
import { cn, formatNum } from '@/lib/utils'

interface AggregateLine {
  id: number
  order_id: number
  order_no: string
  order_date: string
  partner_code: string
  partner_name: string
  item_id: number
  qty: string
  unit_price: string
  supply_amount: string
  total: string
}
interface LinesResponse { lines: AggregateLine[] }
interface ItemSummaryRow {
  item_id: number
  item_code: string
  item_name: string
  spec: string
  unit: string
  qty_total: string
  avg_price: string
  supply_total: string
  vat_total: string
  total: string
}
interface ItemSummaryResp { rows: ItemSummaryRow[] }

export interface ItemAggregateConfig {
  entityShort: string           // '출고' | '입고'
  entityDate: string            // '출고일자' | '입고일자'
  entityNo: string              // '출고번호' | '입고번호'
  partnerLabel: string          // '거래처' | '매입처'
  partnerFilterPlaceholder: string
  emptyDetailText: string       // '이 품목의 출고 내역이 없습니다.'

  summaryApi: string            // '/api/delivery/item-summary'
  summaryExportApi: string      // '/api/delivery/item-summary/export'
  detailApi: string             // '/api/sales-report' | '/api/receiving/lines'
  detailAcceptsSort?: boolean   // sales-report만 sort/order 지원
  queryKeyPrefix: string        // 'item-summary' | 'rcv-item-summary'
  excelFilePrefix: string       // '출고현황_품목별' | '입고현황_품목별'

  linkPath: '/main/delivery' | '/main/receiving'
  partnerBizClass?: 'customer' | 'vendor' | 'both' | ''
}

export function ItemAggregateTab({ config: c }: { config: ItemAggregateConfig }) {
  const push = useToast((s) => s.push)
  const { downloading, download } = useFileDownload()
  // 기간·거래처·검색·선택 품목은 탭을 오가도 유지(스코프 키=queryKeyPrefix → 출고/입고 분리).
  const k = c.queryKeyPrefix
  const [dateFrom, setDateFrom] = useTabField(k, 'dateFrom', '')   // 기본 빈 날짜(직접 지정)
  const [dateTo, setDateTo] = useTabField(k, 'dateTo', '')
  const [partners, setPartners] = useTabField<number[]>(k, 'partners', [])
  const [search, setSearch] = useTabField(k, 'search', '')
  const [selectedItem, setSelectedItem] = useTabField<number | null>(k, 'selectedItem', null)

  async function downloadExcel() {
    if (!items.length) { push('내보낼 데이터가 없습니다', 'warn'); return }
    const p = new URLSearchParams({ from: dateFrom, to: dateTo })
    if (partners.length) p.set('partner', partners.join(','))
    if (search) p.set('q', search)
    await download(`${c.summaryExportApi}?${p}`, `${c.excelFilePrefix}_${dateFrom}_${dateTo}.xlsx`)
  }

  const { data: summary, refetch: refetchSummary, isFetching: fetchingSummary } = useQuery({
    queryKey: [c.queryKeyPrefix, dateFrom, dateTo, partners, search],
    queryFn: () => {
      const p = new URLSearchParams({ from: dateFrom, to: dateTo })
      if (partners.length) p.set('partner', partners.join(','))
      if (search) p.set('q', search)
      return api<ItemSummaryResp>(`${c.summaryApi}?${p}`)
    },
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })
  const items = summary?.rows ?? []

  useEffect(() => {
    if (selectedItem && items.some((r) => r.item_id === selectedItem)) return
    setSelectedItem(items[0]?.item_id ?? null)
  }, [items, selectedItem])

  // detail: 선택 품목의 일자별 내역 — 백엔드에 item filter 없어 코드로 q 검색 후 클라 필터.
  const { data: detail, refetch: refetchDetail, isFetching: fetchingDetail } = useQuery({
    enabled: !!selectedItem,
    queryKey: [`${c.queryKeyPrefix}-detail`, selectedItem, dateFrom, dateTo, partners],
    queryFn: () => {
      const p = new URLSearchParams({ from: dateFrom, to: dateTo })
      if (partners.length) p.set('partner', partners.join(','))
      if (c.detailAcceptsSort) { p.set('sort', 'date'); p.set('order', 'asc') }
      const code = items.find((r) => r.item_id === selectedItem)?.item_code
      if (code) p.set('q', code)
      return api<LinesResponse>(`${c.detailApi}?${p}`)
    },
  })
  const detailLines = (detail?.lines ?? []).filter((l) => l.item_id === selectedItem)

  const selectedRow = items.find((r) => r.item_id === selectedItem)
  const grandTotals = items.reduce(
    (s, r) => ({
      qty:    s.qty + Number(r.qty_total),
      supply: s.supply + Number(r.supply_total),
      total:  s.total + Number(r.total),
    }),
    { qty: 0, supply: 0, total: 0 },
  )

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="mb-4 flex justify-end">
        <button onClick={downloadExcel} disabled={downloading || !items.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>
      <DateRangeBar from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}>
        <RefreshButton busy={fetchingSummary || fetchingDetail}
                       onClick={() => { void refetchSummary(); void refetchDetail() }} />
        <div className="flex-1 min-w-[200px]">
          <MultiPartnerPicker value={partners} onChange={setPartners}
                              bizClass={c.partnerBizClass ?? ''}
                              placeholder={c.partnerFilterPlaceholder}/>
        </div>
        <SearchBox value={search} onChange={setSearch}
                   placeholder="품목 검색 (코드·품명)" className="flex-1 min-w-[200px]"/>
        <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{items.length}품목</span>
      </DateRangeBar>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
        <div className="bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide border-b">
          품목별 합계
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(60vh - 200px)' }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-xs text-slate-500 uppercase sticky top-0">
              <tr>
                <th className="px-2 py-2 w-6"></th>
                <th className="px-2 py-2 text-left font-medium w-24">품목코드</th>
                <th className="px-2 py-2 text-left font-medium">품명</th>
                <th className="px-2 py-2 text-left font-medium w-24">규격(원산지)</th>
                <th className="px-2 py-2 text-center font-medium w-12">단위</th>
                <th className="px-2 py-2 text-right font-medium w-20">수량합계</th>
                <th className="px-2 py-2 text-right font-medium w-24">평균단가</th>
                <th className="px-2 py-2 text-right font-medium w-28">공급가액합계</th>
                <th className="px-2 py-2 text-right font-medium w-28">총합계</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const active = r.item_id === selectedItem
                return (
                  <tr key={r.item_id} onClick={() => setSelectedItem(r.item_id)}
                      className={cn('border-t border-slate-100 cursor-pointer',
                        active ? 'bg-emerald-50' : 'hover:bg-slate-50')}>
                    <td className="px-2 py-1.5">
                      {active && <ChevronRight className="w-3.5 h-3.5 text-emerald-600"/>}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs tabular-nums text-slate-500">{r.item_code}</td>
                    <td className={cn('px-2 py-1.5', active ? 'font-semibold text-emerald-900' : 'text-slate-900')}>
                      {r.item_name}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500 text-xs">{r.spec}</td>
                    <td className="px-2 py-1.5 text-center text-slate-500 text-xs">{r.unit}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(r.qty_total)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{formatNum(r.avg_price)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(r.supply_total)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatNum(r.total)}</td>
                  </tr>
                )
              })}
              {!items.length && (
                <tr><td colSpan={9} className="text-center text-slate-400 py-12 text-sm">데이터 없음</td></tr>
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-emerald-100 font-bold border-t-2 border-emerald-300">
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-right text-xs text-emerald-900 uppercase">합 계</td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-900">{formatNum(grandTotals.qty)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-900">{formatNum(grandTotals.supply)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-900">{formatNum(grandTotals.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide border-b flex items-center justify-between">
          <span>일자별 {c.entityShort} 내역 {selectedRow && `— ${selectedRow.item_name}`}</span>
          <span className="text-slate-400 normal-case tracking-normal">{detailLines.length}건</span>
        </div>
        <div className="overflow-y-auto overflow-x-auto" style={{ maxHeight: '40vh' }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-2 py-2 text-left font-medium w-20">{c.entityDate}</th>
                <th className="px-2 py-2 text-left font-medium w-36">{c.entityNo}</th>
                <th className="px-2 py-2 text-left font-medium">{c.partnerLabel}</th>
                <th className="px-2 py-2 text-right font-medium w-16">수량</th>
                <th className="px-2 py-2 text-right font-medium w-20">단가</th>
                <th className="px-2 py-2 text-right font-medium w-24">공급가액</th>
                <th className="px-2 py-2 text-right font-medium w-24">합계금액</th>
              </tr>
            </thead>
            <tbody>
              {detailLines.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1.5 tabular-nums text-slate-600">{l.order_date}</td>
                  <td className="px-2 py-1.5 font-mono text-xs tabular-nums">
                    <Link to={c.linkPath}
                          search={{ order: l.order_id, from: dateFrom, to: dateTo }}
                          className="text-blue-600 hover:text-blue-800 hover:underline">
                      {l.order_no}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">
                    <span className="font-mono text-xs text-slate-400 mr-1">{l.partner_code}</span>
                    {l.partner_name}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(l.qty)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(l.unit_price)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatNum(l.supply_amount)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatNum(l.total)}</td>
                </tr>
              ))}
              {!detailLines.length && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8 text-sm">
                  {selectedItem ? c.emptyDetailText : '품목을 선택하세요.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
